/**
 * Comptes exploitants (exploitant uniquement).
 *
 * Les intervenants ont leur propre écran : leur compte porte un code de
 * tournée et une boîte aux lettres, qui n'ont pas d'équivalent ici.
 *
 * Un mot de passe se pose mais ne se relit jamais — le serveur n'en garde
 * qu'une empreinte, il n'y a rien à afficher. Le champ reste donc vide et
 * signifie « inchangé » tant qu'on n'y écrit pas.
 */
import { useState } from "react";
import { PASSWORD_MIN_LENGTH, type UserView } from "@soi/contracts";
import { ApiFailure, api } from "../lib/api.js";
import { queryKeys, useMe, useUsers } from "../lib/queries.js";
import { useToast } from "../components/Toast.js";
import { useQueryClient } from "@tanstack/react-query";

/** « jamais » plutôt qu'une case vide : l'information est utile en soi. */
function lastSeen(iso: string | null): string {
  if (!iso) return "jamais";
  return new Date(iso).toLocaleDateString("fr-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function Row({ row, isSelf }: { row: UserView; isSelf: boolean }) {
  const [draft, setDraft] = useState(row);
  const [password, setPassword] = useState("");
  const toast = useToast();
  const qc = useQueryClient();

  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.users });

  const save = async () => {
    try {
      await api.updateUser(row.id, {
        displayName: draft.displayName,
        email: draft.email,
        ...(password ? { password } : {}),
      });
      setPassword("");
      void refresh();
      toast(
        password
          ? `${draft.displayName} enregistré. Ses sessions ouvertes ont été fermées.`
          : `${draft.displayName} enregistré.`,
        "ok",
      );
    } catch (err) {
      toast(err instanceof ApiFailure ? err.message : "L'enregistrement a échoué.", "err");
    }
  };

  const setActive = async (isActive: boolean) => {
    try {
      await api.updateUser(row.id, { isActive });
      void refresh();
      toast(
        isActive
          ? `${row.displayName} peut de nouveau se connecter.`
          : `${row.displayName} désactivé. Le compte reste dans le journal d'audit.`,
        isActive ? "ok" : "annule",
      );
    } catch (err) {
      toast(err instanceof ApiFailure ? err.message : "L'opération a échoué.", "err");
    }
  };

  return (
    <tr data-testid={`user-${row.id}`} style={row.isActive ? undefined : { opacity: 0.55 }}>
      <td>
        <input
          type="text"
          aria-label={`Nom · ${row.email}`}
          value={draft.displayName}
          onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
        />
      </td>
      <td>
        <input
          type="email"
          aria-label={`E-mail · ${row.email}`}
          value={draft.email}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
        />
      </td>
      <td>
        <input
          type="password"
          style={{ width: 150 }}
          placeholder="Inchangé"
          aria-label={`Nouveau mot de passe · ${row.email}`}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </td>
      <td>{lastSeen(row.lastSeenAt)}</td>
      <td>{row.isActive ? "Actif" : "Désactivé"}</td>
      <td>
        <button type="button" className="btn small" onClick={() => void save()}>
          Enregistrer
        </button>{" "}
        {/*
          Se désactiver soi-même déconnecterait l'auteur du clic au coup
          d'après. Le serveur le refuse ; le bouton est masqué pour que le
          refus n'ait pas à être découvert par l'erreur.
        */}
        {!isSelf &&
          (row.isActive ? (
            <button
              type="button"
              className="del-btn"
              title="Désactiver"
              onClick={() => void setActive(false)}
            >
              ✕
            </button>
          ) : (
            <button type="button" className="btn ghost small" onClick={() => void setActive(true)}>
              Réactiver
            </button>
          ))}
      </td>
    </tr>
  );
}

export function Users() {
  const { data: rows, isLoading } = useUsers();
  const { data: me } = useMe();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ displayName: "", email: "", password: "" });
  const toast = useToast();
  const qc = useQueryClient();

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.createUser(form);
      setForm({ displayName: "", email: "", password: "" });
      setCreating(false);
      void qc.invalidateQueries({ queryKey: queryKeys.users });
      toast("Compte exploitant créé.", "ok");
    } catch (err) {
      toast(err instanceof ApiFailure ? err.message : "La création a échoué.", "err");
    }
  };

  return (
    <div className="gestion">
      <div className="admin-main">
        <div className="wrap">
          <section className="panel">
            <h1>Utilisateurs</h1>
            <p className="lede">
              Comptes autorisés à ouvrir l'espace de gestion. Un compte se désactive plutôt qu'il ne
              se supprime : ses traces dans le journal d'audit gardent ainsi un auteur. Changer un
              mot de passe ferme immédiatement les sessions ouvertes de la personne.
            </p>

            <div className="card">
              <div className="card-body" style={{ padding: 0 }}>
                <div className="tbl-wrap" style={{ border: 0, borderRadius: 0 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>E-mail</th>
                        <th>Nouveau mot de passe</th>
                        <th>Dernière activité</th>
                        <th>État</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading && (
                        <tr>
                          <td colSpan={6}>Chargement…</td>
                        </tr>
                      )}
                      {rows?.map((row) => (
                        <Row key={row.id} row={row} isSelf={row.id === me?.id} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="savebar">
                <span className="status" />
                {!creating && (
                  <button type="button" className="btn ghost small" onClick={() => setCreating(true)}>
                    + Ajouter un utilisateur
                  </button>
                )}
              </div>
            </div>

            {creating && (
              <div className="card" style={{ marginTop: 16 }}>
                <form onSubmit={create} className="card-body">
                  <div className="toolbar">
                    <input
                      type="text"
                      placeholder="Nom affiché"
                      aria-label="Nom affiché"
                      value={form.displayName}
                      onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                    />
                    <input
                      type="email"
                      placeholder="prenom.nom@lausanne.ch"
                      aria-label="Adresse e-mail"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                    <input
                      type="password"
                      placeholder={`Mot de passe (${PASSWORD_MIN_LENGTH} caractères minimum)`}
                      aria-label="Mot de passe"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                    />
                    <button type="submit" className="btn small">
                      Créer
                    </button>
                    <button
                      type="button"
                      className="btn ghost small"
                      onClick={() => setCreating(false)}
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
